import { Helmet } from "react-helmet-async";
import { robotsMeta } from "../lib/seo_env";

const SITE = {
  name: "Medical Peptides",
  suffix: "Medical Peptides Powered by VidaVital Medical",
  origin: "https://medicalpeptides.ai",
};
const ORGANIZATION_ID = `${SITE.origin}/#organization`;

/**
 * Universal SEO/AISO wrapper. Renders <title>, meta, canonical, OpenGraph,
 * Twitter cards, and one or more JSON-LD blocks.
 *
 * Title format:  "{title} | Medical Peptides Powered by VidaVital Medical"
 *
 * Props:
 *  - title           string   page title (without suffix; suffix auto-appended)
 *  - description     string   140–160 chars, appears in search results
 *  - path            string   canonical path (defaults to current window path)
 *  - image           string   absolute URL for og:image
 *  - noindex         bool     don't index (admin pages, forms, etc.)
 *  - schema          object | array   JSON-LD structured data
 *  - breadcrumbs     array of {name, path}   builds BreadcrumbList schema
 */
export default function SEO({ title, description, path, image, noindex = false, schema, breadcrumbs }) {
  const pathname = path || (typeof window !== "undefined" ? window.location.pathname : "/");
  const url = SITE.origin + pathname;
  const fullTitle = title ? `${title} | ${SITE.suffix}` : `${SITE.name} | Personalized Peptide Therapy Education`;
  const desc = description || "Evidence-based peptide therapy education. Provider-directed medical care from licensed VidaVital Medical clinicians via live video consultation.";
  const img = image || `${SITE.origin}/social-preview.jpg`;

  const schemas = [];
  // Site-wide Organization / MedicalOrganization
  schemas.push({
    "@context": "https://schema.org",
    "@id": ORGANIZATION_ID,
    "@type": ["Organization", "MedicalOrganization"],
    name: "Medical Peptides",
    alternateName: "MedicalPeptides.ai",
    url: SITE.origin,
    logo: `${SITE.origin}/vv-gold-logo.png`,
    telephone: "+1-888-773-8677",
    sameAs: [
      "https://www.vidavitalmedical.com",
      "https://x.com/medicalpeptidesmedicalpeptides",
      "https://www.facebook.com/profile.php?id=61591829557886",
      "https://www.linkedin.com/company/medical-peptides-powered-by-vidavital-medical",
    ],
    parentOrganization: {
      "@type": "MedicalOrganization",
      name: "VidaVital Medical",
      url: "https://www.vidavitalmedical.com",
    },
  });
  // WebSite + SearchAction
  schemas.push({
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: SITE.name,
    alternateName: "MedicalPeptides.ai",
    url: SITE.origin,
    publisher: { "@id": ORGANIZATION_ID },
    potentialAction: {
      "@type": "SearchAction",
      target: `${SITE.origin}/peptide-library?q={search_term_string}`,
      "query-input": "required name=search_term_string",
    },
  });
  if (breadcrumbs?.length) {
    schemas.push({
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE.origin + "/" },
        ...breadcrumbs.map((b, i) => ({
          "@type": "ListItem",
          position: i + 2,
          name: b.name,
          item: SITE.origin + b.path,
        })),
      ],
    });
  }
  if (Array.isArray(schema)) schemas.push(...schema);
  else if (schema) schemas.push(schema);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      <link rel="canonical" href={url} />
      {noindex ? <meta name="robots" content="noindex, nofollow" /> : <meta name="robots" content={robotsMeta(false)} />}

      {/* Open Graph — SINGLE authoritative set per page (base tags removed from
          public/index.html). Non-JS crawlers see these via the Puppeteer
          build-time prerender step. */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE.suffix} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={img} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content="Medical Peptides — Personalized Peptide Therapy Education, powered by VidaVital Medical" />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      <meta name="twitter:image" content={img} />

      {/* JSON-LD blocks */}
      {schemas.map((s) => (
        <script key={`${s['@type']}-${s.url || s.name || ''}`} type="application/ld+json">{JSON.stringify(s)}</script>
      ))}
    </Helmet>
  );
}
